import { update } from 'state/util';
import { STATUS } from 'state/requestStatus/reducer';

const getKeyAndComments = (state, action) => {
  let key = null;
  if (action.payload && action.payload.parentId) {
    key = `${action.payload.parentId}`;
  }
  else if (action.payload && action.payload.userId && action.payload.pageId) {
    key = `${action.payload.userId}#${action.payload.pageId}`;
  }
  // append the sectionId if we have a section specific lookup, the
  // backend handles returning this to us in the body if we specified
  // a sectionId lookup. the sectionId can either come from the root
  // of the request or inside the data key (on creates)
  let sectionId = null;
  if (key && action.payload.sectionId) {
    sectionId = action.payload.sectionId;
  }
  else if (key && action.payload.data && action.payload.data.sectionId) {
    sectionId = action.payload.data.sectionId;
  }
  // combine the two key sets if all was successful
  if (key && sectionId) {
    key = `${key}#${sectionId}`;
  }
  const currentComments = state[key] ? state[key].data || [] : [];
  return {
    key: key,
    currentComments: currentComments,
  };
}

const getItemIndex = (items, id) => {
  const ixList = items.map((v, i) => {
    if (v.id === id)
      return i;
    else
      return 0;
  })
  if (!ixList) {
    return null;
  }
  return ixList.reduce((a, x) => x + a);
};

/**
 * Given an item, look for the parent state tree object
 * and index of the given parent.
 */
const findParentKey = (item, state) => {
  window.item = item;
  window.state = state;
  let found = false;
  let foundKey = null;
  let foundIndex = null
  let foundComments = null;
  Object.entries(state).forEach((section) => {
    if (found) return;
    const [ commentKey, commentData ]  = section;
    if (!commentData || !commentData.data) return;
    commentData.data.forEach((c, index) => {
      if (c.id === item.parentId) {
        found = true;
        foundKey = commentKey;
        foundIndex = index;
        foundComments = commentData;
      }
    });
  });
  return { found, foundKey, foundIndex, foundComments };
};

/**
 * Since it's possible to be part way down a page and create a comment
 * (where it's then added dynamically w/o fetching from the API), it's
 * possible for dupes to show up in the list. This goes through the
 * list of comments and strips out duplicates based on their ID.
 */
const dedupe = (comments) => {
  const ids = new Set();
  return comments.map((c) => {
    if (ids.has(c.id)) {
      return null;
    } else {
      ids.add(c.id);
      return c;
    }
  }).filter((c) => c);
};

/**
 * Update (mutate) the would be new state with respect to a given
 * item and update the parent relationship based on an operation.
 * For example: given the current comments, and we've successfully
 * deleted a reply, update the parent comment's replies count.
 *
 * To reiterate, this *mutates* newState. Make sure this _is not_
 * the state object itself. Always make a copy of the state object
 * using Object.assign({}, state) before passing to this.
 */
const updateParentState = (newState, state, payload, operation) => {
  const {
    found, foundKey, foundIndex, foundComments
  } = findParentKey(payload, state);
  if (found) {
    const decrementOther = Object.assign({}, foundComments);
    switch(operation) {
      case "replies -=":
        decrementOther.data[foundIndex].replies -= 1;
        break;
      case "replies +=":
        decrementOther.data[foundIndex].replies += 1;
        break;
      // This is not a thing right now
      //case "upvotes -=":
      //  decrementOther.data[foundIndex].upvotes -= 1;
      //  break;
      case "upvotes +=":
        decrementOther.data[foundIndex].upvotes += 1;
        break;
      default:
        throw new Error(`Invalid updateParentState operation ${operation}`);
    }
    newState[foundKey] = decrementOther;
  }
}

/**
 * Storage for comments. Both owner and others'. We will
 * drive the show/hide edit & delete buttons via the userId
 * key and whether or not it's the currently authed users'.
 *
 * comments state sub-tree:
 * {
 *   "pageOwnerUserID#candidate-page-id": {
 *     data: [{
 *         data: {
 *           text: "some comment",
 *           sectionId: "100",
 *       },
 *       parentId: "PageOwnerId#PageId",
 *       id: "020#1524534008.533" <- {sectionId}#{timestamp}
 *       createdAt: timestamp,
 *       updatedAt: timestamp,
 *     },{
 *     ... some other comments ...,
 *     ],
 *     parentId: "UserPageId#PageId",
 *     status: STATUS.COMPLETE,
 *     message: null || "some status/error message"
 *   },
 *   ... other page comments ...,
 * }
 *
 * Action Dispatch Structure:
 *  {
 *    type: "LIST_ALL_COMMENTS_REQUESTED",
 *    payload: {
 *      parentId: "UserId#prop-page-1",
 *      data: {
 *        sectionId: "010",
 *        text: "Some comment text",
 *      }
 *    }
 *  }
 */

// we add page keys to this as we add preferences
const initialState = {};

export const commentsReducer = (state, action) => {
  if (state === undefined)
    return initialState;

  const { key, currentComments } = getKeyAndComments(state, action);

  //if (action.type.match(/.*COMMENT.*/)) {
  //  console.log("Action", action.type, "Key", key, "Payload", action.payload);
  //  console.log("Current state", state);
  //}

  switch(action.type) {
    case "LOGOUT_REQUESTED":
      return initialState;

    case "LIST_COMMENTS_REQUESTED":
    case "LIST_ALL_COMMENTS_REQUESTED":
      return update(state, {
        [key]: {
          message: "Loading...",
          status: STATUS.LOADING,
        },
      });

    case "LIST_COMMENTS_CLEARDATA":
    case "LIST_ALL_COMMENTS_CLEARDATA":
      return update(state, {
        [key]: {
          data: [],
          user: {},
          status: STATUS.LOADING,
        },
      }, true);

    case "LIST_COMMENTS_SUCCESSFUL":
    case "LIST_ALL_COMMENTS_PAGE_SUCCESSFUL":
      return update(state, {
        [key]: {
          data: dedupe(currentComments.concat(action.payload.data)),
          user: action.payload.user,
          status: STATUS.LOADING,
          next: action.payload.page.next,
        },
      });

    case "LIST_ALL_COMMENTS_SUCCESSFUL":
      return update(state, {
        [key]: {
          data: dedupe(currentComments.concat(action.payload.data)),
          user: action.payload.user,
          message: null,
          status: STATUS.COMPLETE,
        },
      });

    case "CREATE_COMMENT_REQUESTED":
      return update(state, {
        [key]: {
          message: "Creating comment...",
          status: STATUS.SAVING,
        },
      });

    case "CREATE_COMMENT_SUCCESSFUL":
      const newCreateState = {
        [key]: {
          data: currentComments.concat([action.payload]),
          message: "Comment saved",
          status: STATUS.COMPLETE,
        },
      }
      // make sure this isn't a reply
      if (!action.payload.sectionId && !action.payload.data.sectionId) {
        updateParentState(newCreateState, state, action.payload, "replies +=");
      }
      return update(state, newCreateState);

    case "DELETE_COMMENT_REQUESTED":
      return update(state, {
        [key]: {
          message: "Deleting comment...",
          status: STATUS.LOADING,
        },
      });

    case "DELETE_COMMENT_SUCCESSFUL":
      const delIndex = getItemIndex(currentComments, action.payload.id);
      const withoutDeleted = [...currentComments];
      withoutDeleted.splice(delIndex, 1);
      const newDeleteState = {
        [key]: {
          data: withoutDeleted,
          message: "Comment deleted",
          status: STATUS.COMPLETE,
        },
      }
      updateParentState(newDeleteState, state, action.payload, "replies -=");
      return update(state, newDeleteState);

    case "UPDATE_COMMENT_REQUESTED":
      return update(state, {
        [key]: {
          message: "Updating comment...",
          status: STATUS.SAVING,
        },
      });

    case "UPDATE_COMMENT_SUCCESSFUL":
      const updComment = action.payload;
      const updIndex = getItemIndex(currentComments, updComment.id);
      const withUpdated = [...currentComments];
      withUpdated.splice(updIndex, 1, updComment);
      return update(state, {
        [key]: {
          data: withUpdated,
          message: "Comment saved",
          status: STATUS.COMPLETE,
        },
      });

    case "UPVOTE_COMMENT_REQUESTED":
      return update(state, {
        [key]: {
          message: "Upvoting comment...",
          status: STATUS.SAVING,
        },
      });

    case "UPVOTE_COMMENT_SUCCESSFUL":
      const upResponse = action.payload;
      const didUpvote = upResponse.message === "Voted";
      const upvIndex = getItemIndex(currentComments, upResponse.id);
      const withUpv = [...currentComments];
      // only change upvotes if we actually incremented the
      // upvote in the DB (response.message will be "Voted")
      if (didUpvote) {
        const currentUpv = withUpv[upvIndex].upvotes;
        withUpv[upvIndex].upvotes = !currentUpv ? 1 : currentUpv + 1;
      }
      const newUpdateState = {
        [key]: {
          data: withUpv,
          message: upResponse.message,
          status: STATUS.COMPLETE,
        },
      };
      updateParentState(newUpdateState, state, action.payload, "replies +=");
      return update(state, newUpdateState);

    case "DELETE_COMMENT_FAILED":
    case "CREATE_COMMENT_FAILED":
    case "UPDATE_COMMENT_FAILED":
    case "UPVOTE_COMMENT_FAILED":
    case "LIST_ALL_COMMENTS_FAILED":
      const errParentId = action.payload.parentId;
      if (action.payload.statusCode === 404) {
        // return a blank vote pref for creating if not found
        return update(state, {
          [errParentId]: {
            message: null,
            data: [],
            status: STATUS.NONE,
          },
        });
      }

      return update(state, {
        [key]: {
          message: action.payload.message,
          status: STATUS.FAILED,
        },
      });

    default:
      return state;
  }
};




// WEBPACK FOOTER //
// ./src/state/comments/reducer.js